import React from "react";
import { Modal, Button, Badge } from "react-bootstrap";

const LeaveRequestsModal = ({ show, handleClose, leaveRequest }) => {
  const getStatusVariant = (status) => {
    switch (status) {
      case "pending":  return "warning";
      case "approved": return "success";
      case "rejected": return "danger";
      default:         return "secondary";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // ✅ NEW — formats "2026-02-17T09:00" or "2026-02-17 09:00:00" nicely
  const formatDateTime = (datetimeString) => {
    if (!datetimeString) return "—";
    try {
      const date = new Date(datetimeString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
    } catch {
      return datetimeString;
    }
  };

  const parseLeaveDetails = (request) => {
    if (!request) {
      return {
        dates: [], days: [], types: [], totalDays: 0,
        wfhdates: [], wfhdays: [], totalWFHDays: 0,
        compWorkingDates: [], compOffDates: [], compDurations: [], totalCompDays: 0
      };
    }

    const dates = request.leave_dates
      ? request.leave_dates.split(",").map(d => d.trim()) : [];

    const wfhdates = request.wfh_dates
      ? request.wfh_dates.split(",").map(d => d.trim()) : [];

    const days = request.leave_days
      ? request.leave_days.split(",").map(d => d.trim()) : [];

    const wfhdays = request.wfh_days
      ? request.wfh_days.split(",").map(d => d.trim()) : [];

    const types = request.leave_day_types
      ? request.leave_day_types.split(",").map(t => t.trim()) : [];

    const compWorkingDates = request.compensatory_working_dates
      ? request.compensatory_working_dates.split(",").map(d => d.trim()).filter(Boolean) : [];

    const compOffDates = request.compensatory_off_dates
      ? request.compensatory_off_dates.split(",").map(d => d.trim()) : [];

    const compDurations = request.compensatory_durations
      ? request.compensatory_durations.split(",").map(d => d.trim()).filter(Boolean) : [];

    const compRequestTypes = request.compensatory_request_types
      ? request.compensatory_request_types.split(",").map(d => d.trim()) : [];

    const totalDays = days.reduce((sum, day) => {
      return day.toLowerCase() === "half" ? sum + 0.5 : sum + 1;
    }, 0);

    const totalWFHDays = wfhdays.reduce((sum, day) => {
      return day.toLowerCase() === "half" ? sum + 0.5 : sum + 1;
    }, 0);

    const totalCompDays = compDurations.reduce((sum, dur) => {
      const v = dur?.toLowerCase() || "";
      if (v.includes("half")) return sum + 0.5;
      return sum + 1;
    }, 0);

    return {
      dates, days, types, totalDays,
      wfhdates, wfhdays, totalWFHDays,
      compWorkingDates, compOffDates, compDurations, compRequestTypes, totalCompDays
    };
  };

  // ✅ NEW — parses travel columns from the SQL query result
  const parseTravelDetails = (request) => {
    if (!request?.travel_locations) return [];

    const locations    = request.travel_locations.split(", ").map(d => d.trim());
    const fromDTs      = request.travel_from_datetimes?.split(", ").map(d => d.trim()) || [];
    const toDTs        = request.travel_to_datetimes?.split(", ").map(d => d.trim())   || [];
    const approvedList = request.travel_approved_statuses?.split(", ").map(s => s.trim() === "1") || [];

    return locations.map((loc, i) => ({
      location:     loc,
      fromDateTime: fromDTs[i]      || "",
      toDateTime:   toDTs[i]        || "",
      approved:     approvedList[i] || false
    }));
  };

  const calculateTotalDays = (data) => {
    if (Array.isArray(data)) {
      return data.reduce((total, rec) => {
        if (!rec?.leaveDay) return total + 1;
        return rec.leaveDay.toLowerCase() === "half" ? total + 0.5 : total + 1;
      }, 0);
    }
    if (typeof data === "object" && data !== null && data.leave_type === "Work From Home Request") {
      return parseLeaveDetails(data).totalWFHDays;
    }
    if (typeof data === "object" && data !== null) {
      return parseLeaveDetails(data).totalDays;
    }
    return 0;
  };

  const leaveDetails  = parseLeaveDetails(leaveRequest);
  const travelRecords = parseTravelDetails(leaveRequest);

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header className="bg-gradient bg-light pb-3">
        <div className="d-flex align-items-center w-100">
          <div className="flex-grow-1 pe-3">
            <Modal.Title
              className="h5 mb-2"
              style={{ wordWrap: "break-word", wordBreak: "break-word", overflowWrap: "break-word" }}
            >
              {leaveRequest?.subject}
            </Modal.Title>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <small className="text-muted">{formatDate(leaveRequest?.created_at)}</small>
              <Badge bg="info" className="text-uppercase px-2 py-1" style={{ wordBreak: "keep-all" }}>
                {leaveRequest?.leave_type || "—"}
              </Badge>
              <Badge bg={getStatusVariant(leaveRequest?.status)} className="text-uppercase px-2 py-1">
                {leaveRequest?.status || "Unknown"}
              </Badge>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-3">

        {/* Email Body */}
        {leaveRequest?.body && leaveRequest.body.trim() !== "" && (
          <div className="mb-3">
            <h6 className="small fw-bold mb-1">Message</h6>
            <p
              className="p-2 bg-light rounded small border mb-0"
              style={{ whiteSpace: "pre-wrap", maxHeight: "200px", overflowY: "auto" }}
            >
              {leaveRequest.body}
            </p>
          </div>
        )}

        {/* WFH Details */}
        {leaveRequest?.wfh_dates && leaveRequest.wfh_dates.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small fw-bold mb-0">WFH Details</h6>
              <Badge bg="info" className="small px-2 py-1">
                {leaveDetails.totalWFHDays} day{leaveDetails.totalWFHDays !== 1 ? "s" : ""}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: "0.7rem" }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-6 py-1 border-end px-2 fw-bold text-dark">Date</div>
                <div className="col-6 py-1 text-center fw-bold text-dark">Duration</div>
              </div>
              {leaveDetails.wfhdates.map((date, i) => (
                <div key={i} className="row g-0 border-bottom">
                  <div className="col-6 py-1 border-end px-2 fw-medium">{formatDate(date)}</div>
                  <div className="col-6 py-1 text-center">{leaveDetails.wfhdays[i] || "Full"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leave Details */}
        {leaveRequest?.leave_dates && leaveRequest.leave_dates.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small fw-bold mb-0">Leave Details</h6>
              <Badge bg="info" className="small px-2 py-1">
                {leaveDetails.totalDays} day{leaveDetails.totalDays !== 1 ? "s" : ""}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: "0.7rem" }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-5 py-1 border-end px-2 fw-bold text-dark">Date</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">Type</div>
                <div className="col-3 py-1 text-center fw-bold text-dark">Day</div>
              </div>
              {leaveDetails.dates.map((date, i) => (
                <div key={i} className="row g-0 border-bottom">
                  <div className="col-5 py-1 border-end px-2 fw-medium">{formatDate(date)}</div>
                  <div className="col-4 py-1 border-end text-center">{leaveDetails.types[i] || "—"}</div>
                  <div className="col-3 py-1 text-center">{leaveDetails.days[i] || "Full"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compensatory Off Details */}
        {leaveDetails.compWorkingDates.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small fw-bold mb-0">Compensatory Off Details</h6>
              <Badge bg="warning" text="dark" className="small px-2 py-1">
                {leaveDetails.totalCompDays} day{leaveDetails.totalCompDays !== 1 ? "s" : ""}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: "0.7rem" }}>
              {leaveRequest?.leave_type === "Compensatory Off (Cash)" ? (
                <>
                  <div className="row g-0 border-bottom bg-light">
                    <div className="col-6 py-1 border-end px-2 fw-bold text-dark">Worked On</div>
                    <div className="col-6 py-1 text-center fw-bold text-dark">Duration</div>
                  </div>
                  {leaveDetails.compWorkingDates.map((workDate, i) => (
                    <div key={i} className="row g-0 border-bottom">
                      <div className="col-6 py-1 border-end px-2 fw-medium">{formatDate(workDate)}</div>
                      <div className="col-6 py-1 text-center">{leaveDetails.compDurations[i] || "Full Day"}</div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="row g-0 border-bottom bg-light">
                    <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Working Date</div>
                    <div className="col-4 py-1 border-end text-center fw-bold text-dark">Comp Off Date</div>
                    <div className="col-4 py-1 text-center fw-bold text-dark">Duration</div>
                  </div>
                  {leaveDetails.compWorkingDates.map((workDate, i) => (
                    <div key={i} className="row g-0 border-bottom">
                      <div className="col-4 py-1 border-end px-2 fw-medium">{formatDate(workDate)}</div>
                      <div className="col-4 py-1 border-end text-center">{formatDate(leaveDetails.compOffDates[i]) || "—"}</div>
                      <div className="col-4 py-1 text-center">{leaveDetails.compDurations[i] || "Full Day"}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* ✅ Travel (Site Visit) Details */}
        {travelRecords.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small fw-bold mb-0">🚗 Travel (Site Visit) Details</h6>
              <Badge bg="warning" text="dark" className="small px-2 py-1">
                {travelRecords.length} record{travelRecords.length !== 1 ? "s" : ""}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: "0.7rem" }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Location</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">From</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">To</div>
              </div>
              {travelRecords.map((record, i) => (
                <div key={i} className="row g-0 border-bottom">
                  <div className="col-4 py-1 border-end px-2 fw-medium">{record.location}</div>
                  <div className="col-4 py-1 border-end text-center">{formatDateTime(record.fromDateTime)}</div>
                  <div className="col-4 py-1 border-end text-center">{formatDateTime(record.toDateTime)}</div>
                 
                </div>
              ))}
            </div>
          </div>
        )}

      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LeaveRequestsModal;